import { Icon } from "@chakra-ui/react";
import { MdCardMembership } from "react-icons/md";
import MiniStatistics from "../../../components/card/MiniStatistics";
import IconBox from "../../../components/icons/IconBox";
import { translate } from "../../../i18n";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { getDemoGrowth, getDemoSubscriptionCount, isDemoOrganization } from "../demoAnalyticsData";
import { useGetSubscriptionCount } from "../hooks/useGetSubscriptionCount";
import { computeGrowth } from "../utils/computeGrowth";
import { UNKNOWN_STAT_VALUE, isUnreadable, statPlaceholder } from "../utils/statValue";

type Props = {
    boxColor: string;
    brandColor: string;
    startDate: Date;
    endDate: Date;
    previousStartDate: Date | null;
    previousEndDate: Date | null;
    growthLabel?: string;
};

export default function SubscriptionCountCard({ boxColor, brandColor, startDate, endDate, previousStartDate, previousEndDate, growthLabel }: Props) {
    const { selectedOrganization } = useSelectedOrganization();
    const isDemo = isDemoOrganization(selectedOrganization?.id);
    /**
     * Every `analytics/*` route carries `@UseGuards(AdminAuthGuard)` — read from
     * `api/src/analytics/analytics.controller.ts` this run, not inherited — so a
     * stale dashboard token or an operator who is no longer an admin of this org
     * is REFUSED, with no outage at all, on top of the ordinary 5xx/network
     * paths. All of them leave `data === undefined`, which this card used to
     * render as a real number.
     */
    const { data, isInitialLoading: isPending, isError } = useGetSubscriptionCount(selectedOrganization?.id);
    // Comparison reads: they feed `computeGrowth` only, which returns undefined
    // when either side is undefined, so a failed read removes the growth chip
    // rather than mis-stating it. Allowlisted in `analytics-read-error-gate`.
    const { data: currentData } = useGetSubscriptionCount(
        selectedOrganization?.id,
        startDate,
        endDate,
    );
    const { data: previousData } = useGetSubscriptionCount(
        selectedOrganization?.id,
        previousStartDate ?? undefined,
        previousEndDate ?? undefined,
    );

    const orgId = selectedOrganization?.id;
    const unreadable = isUnreadable(isError, data);
    const demoValue = isDemo ? getDemoSubscriptionCount(startDate, endDate, orgId) : 0;
    const growth = isDemo
        ? getDemoGrowth(demoValue, previousStartDate, previousEndDate, getDemoSubscriptionCount, orgId)
        : previousStartDate ? computeGrowth(currentData?.value, previousData?.value) : undefined;
    /**
     * `Boolean(data)`, not `Boolean(data?.value)`, and a placeholder rather than
     * a literal `'0'` in the last arm — the same pair as `UserCountCard`, for
     * the same reason: this read is gated on `!!organizationId`, and with no
     * organization selected (a refused or empty `my-organizations`) the query is
     * DISABLED, which React Query v4 reports as `isLoading: true` forever. An
     * organization that genuinely sells no subscriptions still shows `0`,
     * because the API answers `{ value: 0 }` — an object.
     */
    const value = isDemo ? demoValue : unreadable ? UNKNOWN_STAT_VALUE : Boolean(data) ? data?.value : statPlaceholder(isPending);

    return (
        <MiniStatistics
            startContent={
                <IconBox
                    w='56px'
                    h='56px'
                    bg={boxColor}
                    icon={<Icon w='32px' h='32px' as={MdCardMembership} color={brandColor} />}
                />
            }
            name={translate('analytics.subscriptions')}
            value={value}
            isError={!isDemo && unreadable}
            growth={growth}
            growthLabel={growthLabel}
        />
    );
}
